/**
 * O que a tela de manutenção DIZ sobre um vencimento — fora do componente,
 * onde um teste alcança. Este projeto não renderiza tela em teste; a frase que
 * decide se alguém para a máquina hoje não pode ficar sem teste por isso.
 *
 * Nenhuma conta de vencimento mora aqui. O servidor calcula (view
 * `manut.vencimento`); a tela só nomeia e formata. Refazer a conta neste
 * arquivo seria a segunda cópia da fórmula — e a cópia duplicada já deixou um
 * defeito passar por doze asserções no módulo 03.
 */
import { formatarData, formatarDecimal, formatarInteiro } from '@/lib/formato';

import type { Confianca, EstadoDoVencimento, Gatilho, VencimentoDaApi } from './api';

/** Rótulo escrito, sempre — nunca só a cor (regra 2 do manual). */
export const ROTULO_DO_ESTADO: Readonly<Record<EstadoDoVencimento, string>> = {
  vencida: 'Vencida',
  vence_logo: 'Vence logo',
  em_dia: 'Em dia',
  sem_leitura: 'Sem leitura',
  sem_ponto_de_partida: 'Sem ponto de partida',
};

/**
 * A cor da data (R02, p.31): vencida em vermelho, próxima em âmbar. Os outros
 * estados não pedem cor, e aí volta `null` — quem chama mantém a cor que já
 * tinha. Recebe `string` porque o painel e o prontuário trazem o estado sem
 * tipo; estado fora do vocabulário não ganha cor.
 */
export function corDoEstado(estado: string): 'text-data-vencida' | 'text-data-proxima' | null {
  if (estado === 'vencida') return 'text-data-vencida';
  if (estado === 'vence_logo') return 'text-data-proxima';
  return null;
}

export const ROTULO_DA_CONFIANCA: Readonly<Record<Confianca, string>> = {
  alta: 'Confiança alta',
  media: 'Confiança média',
  baixa: 'Confiança baixa',
};

export const UNIDADE_DO_GATILHO: Readonly<Record<Gatilho, string>> = {
  horas: 'h',
  km: 'km',
  dias: 'dias',
};

export const MEDIDOR_DO_GATILHO: Readonly<Record<Gatilho, string | null>> = {
  horas: 'horímetro',
  km: 'odômetro',
  dias: null,
};

function numeroDoGatilho(valor: number, gatilho: Gatilho): string {
  return gatilho === 'dias' ? formatarInteiro(Math.abs(valor)) : formatarDecimal(Math.abs(valor), 1);
}

/**
 * "faltam 50,0 h", "passou 12 dias", "vence agora".
 *
 * O sinal vira PALAVRA. "-12 dias" numa tabela se lê como erro de digitação;
 * "passou 12 dias" se lê como o que é.
 */
export function textoDoFaltam(v: Pick<VencimentoDaApi, 'faltam' | 'gatilho'>): string | null {
  if (v.faltam === null) return null;
  const unidade = UNIDADE_DO_GATILHO[v.gatilho];
  if (v.faltam === 0) return 'vence agora';
  const numero = numeroDoGatilho(v.faltam, v.gatilho);
  return v.faltam > 0 ? `faltam ${numero} ${unidade}` : `passou ${numero} ${unidade}`;
}

/** `aaaa-mm-dd` sem virar o dia anterior no fuso de Brasília. */
export function dataDoDia(dia: string): Date {
  return new Date(`${dia.slice(0, 10)}T12:00:00`);
}

/**
 * De onde a previsão vem — a frase que o v6 exige ao lado dela: "mostra a data
 * da última leitura". Sem ela, uma previsão feita sobre um horímetro parado há
 * três semanas parece tão boa quanto uma de ontem.
 */
export function baseDaPrevisao(v: VencimentoDaApi, hoje: Date = new Date()): string {
  if (v.estado === 'sem_ponto_de_partida') {
    return 'Registre quando esta tarefa foi feita pela última vez para o sistema começar a contar.';
  }
  if (v.gatilho === 'dias') return 'Pelo calendário, a partir da última realização.';
  const medidor = MEDIDOR_DO_GATILHO[v.gatilho] ?? 'medidor';
  if (v.leituraAtualEm === null) {
    return `Nenhuma leitura de ${medidor} desta máquina. Ela entra com o apontamento ou o abastecimento.`;
  }
  const leitura = new Date(v.leituraAtualEm);
  const dias = Math.max(0, Math.floor((hoje.getTime() - leitura.getTime()) / 86_400_000));
  const idade = dias === 0 ? 'hoje' : dias === 1 ? 'há 1 dia' : `há ${formatarInteiro(dias)} dias`;
  const ritmo =
    v.ritmoDia === null
      ? 'sem ritmo recente para projetar'
      : `ritmo de ${formatarDecimal(v.ritmoDia, 1)} ${UNIDADE_DO_GATILHO[v.gatilho]}/dia`;
  return `Leitura de ${formatarData(leitura)} (${idade}) · ${ritmo}.`;
}

/**
 * A mesma base, CURTA, para a segunda linha da célula (p.18: segunda linha
 * curta, só o dado): "leitura 21/09 · 11,1 h/dia". A frase inteira de
 * `baseDaPrevisao` vai no `title` da célula. A frase longa fazia a coluna
 * Previsão pedir 348 px e empurrava o Estado para fora do quadro (R01, p.28).
 * Sem ponto de partida não há base: `null`, e a instrução fica acima da lista.
 */
export function baseCurtaDaPrevisao(v: VencimentoDaApi, hoje: Date = new Date()): string | null {
  if (v.estado === 'sem_ponto_de_partida') return null;
  if (v.gatilho === 'dias') return 'pelo calendário';
  const medidor = MEDIDOR_DO_GATILHO[v.gatilho] ?? 'medidor';
  if (v.leituraAtualEm === null) return `sem leitura do ${medidor}`;
  const data = formatarData(new Date(v.leituraAtualEm));
  /* No ano corrente, dia e mês bastam; leitura de outro ano leva o ano, para
     um horímetro parado desde dezembro não parecer de ontem. */
  const dia = /^\d{2}\/\d{2}\/\d{4}$/.test(data) && data.slice(6) === formatarData(hoje).slice(6) ? data.slice(0, 5) : data;
  const ritmo = v.ritmoDia === null ? 'sem ritmo' : `${formatarDecimal(v.ritmoDia, 1)} ${UNIDADE_DO_GATILHO[v.gatilho]}/dia`;
  return `leitura ${dia} · ${ritmo}`;
}

export interface ContagemDaFila {
  readonly vencidas: number;
  readonly venceLogo: number;
  readonly semPontoDePartida: number;
  readonly semLeitura: number;
  readonly maquinasComVencida: number;
}

/** A faixa do topo: quantas tarefas em cada estado, e quantas MÁQUINAS têm vencida. */
export function contarFila(vencimentos: readonly VencimentoDaApi[]): ContagemDaFila {
  const conta = (estado: EstadoDoVencimento) => vencimentos.filter((v) => v.estado === estado).length;
  return {
    vencidas: conta('vencida'),
    venceLogo: conta('vence_logo'),
    semPontoDePartida: conta('sem_ponto_de_partida'),
    semLeitura: conta('sem_leitura'),
    maquinasComVencida: new Set(vencimentos.filter((v) => v.estado === 'vencida').map((v) => v.equipamentoId)).size,
  };
}

/** "a cada 250,0 h", "a cada 365 dias". */
export function textoDoIntervalo(gatilho: Gatilho, intervalo: number): string {
  return `a cada ${numeroDoGatilho(intervalo, gatilho)} ${UNIDADE_DO_GATILHO[gatilho]}`;
}
